/**
 * 追问（annotation）核心纯逻辑：选区偏移、节点解析、range 还原、rect 合并。
 * 移植自 COSMO-Sphere-Client packages/ui/src/components/annotations/annotation-core.ts
 */

export const ANNOTATION_PREFIX_SUFFIX_WINDOW = 24
export const SELECTION_POINTER_MAX_AGE_MS = 1500

const OVERLAY_SELECTOR = '[data-ca-annotation-overlay], [data-ca-annotation-index]'

export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

export function hasExistingTextRangeAnnotation(annotations, start, end) {
  return (annotations || []).some((annotation) => {
    if (!annotation || !annotation.target) return false
    const position = annotation.target.position
    if (!position) return false
    return position.start <= start && position.end >= end
  })
}

export function createSelectionPreviewAnnotation({ sessionId, messageId, startOffset, endOffset, quote }) {
  return {
    id: '__pending-selection-preview__',
    schemaVersion: 1,
    intent: 'highlight',
    body: [{ type: 'highlight' }],
    target: {
      source: { sessionId: sessionId || '', messageId: messageId || '' },
      position: { start: startOffset, end: endOffset },
      quote,
      selectors: []
    },
    style: { color: 'yellow' },
    meta: { ephemeral: true }
  }
}

export function createTextSelectionAnnotation({
  sessionId,
  messageId,
  startOffset,
  endOffset,
  quote,
  note
}) {
  const now = Date.now()
  const trimmedQuote = typeof quote === 'string' ? quote.trim() : ''
  const normalizedNote = typeof note === 'string' ? note.trim() : ''
  const body = [{ type: 'highlight' }]
  if (normalizedNote) {
    body.push({ type: 'note', text: normalizedNote })
  }
  return {
    id: `ann-${now}-${Math.random().toString(36).slice(2, 8)}`,
    schemaVersion: 1,
    intent: normalizedNote ? 'comment' : 'highlight',
    body,
    target: {
      source: { sessionId: sessionId || '', messageId: messageId || '' },
      position: { start: startOffset, end: endOffset },
      quote: trimmedQuote,
      selectors: buildSelectors(trimmedQuote, startOffset, endOffset)
    },
    style: { color: 'yellow' },
    meta: normalizedNote
      ? {
        followUp: {
          text: normalizedNote,
          createdAt: new Date().toISOString()
        }
      }
      : {},
    updatedAt: new Date().toISOString()
  }
}

function buildSelectors(quote, start, end) {
  const selectors = []
  if (typeof start === 'number' && typeof end === 'number') {
    selectors.push({ type: 'text-position', start, end })
  }
  if (quote) {
    selectors.push({ type: 'text-quote', quote, prefix: '', suffix: '' })
  }
  return selectors
}

/**
 * 收集 root 内的文本节点（跳过 overlay / index 角标）。
 */
export function collectTextSegments(root) {
  if (!root) return []
  const segments = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT
      const parent = node.parentElement
      if (parent && parent.closest(OVERLAY_SELECTOR)) {
        return NodeFilter.FILTER_REJECT
      }
      return NodeFilter.FILTER_ACCEPT
    }
  })
  let offset = 0
  let current = walker.nextNode()
  while (current) {
    const text = current.nodeValue || ''
    segments.push({ node: current, start: offset, end: offset + text.length })
    offset += text.length
    current = walker.nextNode()
  }
  return segments
}

export function getCanonicalText(root) {
  return collectTextSegments(root)
    .map((segment) => segment.node.nodeValue || '')
    .join('')
}

export function resolveNodeOffset(segments, container, offset) {
  if (!container) return 0
  if (segments.length === 0) return 0

  // 文本节点：offset 即文本内偏移
  if (container.nodeType === Node.TEXT_NODE) {
    for (let i = 0; i < segments.length; i += 1) {
      if (segments[i].node === container) {
        const length = container.nodeValue ? container.nodeValue.length : 0
        return segments[i].start + Math.min(offset, length)
      }
    }
    return 0
  }

  // 元素节点：offset 是子节点索引，取该位置往后第一个文本节点的起点
  if (container.nodeType === Node.ELEMENT_NODE) {
    const children = container.childNodes || []
    for (let i = offset; i < children.length; i += 1) {
      const child = children[i]
      const firstSegment = segments.find(
        (segment) => child === segment.node || (child.contains && child.contains(segment.node))
      )
      if (firstSegment) return firstSegment.start
    }
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      if (container.contains && container.contains(segments[i].node)) return segments[i].end
    }
  }

  return 0
}

export function resolveRangeFromOffsets(root, segments, start, end) {
  if (!root || segments.length === 0) return null
  const findNodeAt = (offset) => {
    for (let i = 0; i < segments.length; i += 1) {
      const segment = segments[i]
      if (offset >= segment.start && offset <= segment.end) {
        return { node: segment.node, offset: offset - segment.start }
      }
    }
    const last = segments[segments.length - 1]
    return { node: last.node, offset: (last.node.nodeValue || '').length }
  }
  const startPoint = findNodeAt(start)
  const endPoint = findNodeAt(end)
  if (!startPoint || !endPoint) return null
  try {
    const range = document.createRange()
    range.setStart(startPoint.node, startPoint.offset)
    range.setEnd(endPoint.node, endPoint.offset)
    return range
  } catch (error) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[followUp] resolveRangeFromOffsets failed', error)
    }
    return null
  }
}

export function getClientRectsForOffsets(root, segments, start, end) {
  const range = resolveRangeFromOffsets(root, segments, start, end)
  if (!range) return []
  const rects = Array.from(range.getClientRects()).filter(
    (rect) => rect.width > 0.5 && rect.height > 0.5
  )
  if (rects.length === 0) {
    const bounding = range.getBoundingClientRect()
    if (bounding && bounding.width > 0.5 && bounding.height > 0.5) {
      return [bounding]
    }
  }
  return rects
}

/**
 * 按行合并 rect，避免一行被拆成多个碎片。
 */
export function consolidateRectsByLine(rects, tolerance = 2) {
  if (!rects || rects.length === 0) return []
  const sorted = rects
    .map((rect) => ({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    }))
    .sort((a, b) => (Math.abs(a.top - b.top) <= tolerance ? a.left - b.left : a.top - b.top))

  const merged = []
  sorted.forEach((rect) => {
    const last = merged[merged.length - 1]
    if (
      last &&
      Math.abs(last.top - rect.top) <= tolerance &&
      Math.abs(last.height - rect.height) <= tolerance + 2 &&
      rect.left <= last.left + last.width + tolerance
    ) {
      const right = Math.max(last.left + last.width, rect.left + rect.width)
      last.left = Math.min(last.left, rect.left)
      last.width = right - last.left
      last.height = Math.max(last.height, rect.height)
      return
    }
    merged.push({ ...rect })
  })
  return merged
}
